import { useState } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { Loader2, MailCheck, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useForgotPassword } from '@/hooks/use-auth';
import { isAuthenticated } from '@/lib/auth-store';

export const Route = createFileRoute('/forgot-password')({
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/' });
    }
  },
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const forgot = useForgotPassword();
  const [email, setEmail] = useState('');

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    forgot.mutate(email, {
      onSuccess: (r) => {
        if (r.dev_reset_url) console.info('Dev reset URL:', r.dev_reset_url);
      },
    });
  }

  return (
    <div className="grid min-h-screen place-items-center bg-blaze-base px-4 text-foreground">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <img src="/blaze-mark.svg" alt="BlazeIPAM" className="size-10" />
          <h1 className="text-lg font-bold tracking-tight">Restablecer contraseña</h1>
        </div>

        <div className="rounded-lg border border-border bg-blaze-elev1 p-5 shadow-xl">
          {forgot.isSuccess ? (
            <div className="space-y-3 text-center">
              <div className="mx-auto grid size-11 place-items-center rounded-full bg-muted/40">
                <MailCheck className="size-5 text-[var(--success)]" />
              </div>
              <p className="text-sm font-medium">Revisa tu correo</p>
              <p className="text-xs text-muted-foreground">
                Si existe una cuenta con <span className="font-mono">{email}</span>, te enviamos un
                enlace para restablecer tu contraseña. El enlace expira en 1 hora.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Ingresa tu correo y te enviaremos un enlace para crear una contraseña nueva.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="email">Correo</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="noc@tu-isp.do"
                  autoComplete="email"
                  required
                />
              </div>
              <Button type="submit" className="w-full gap-2" disabled={forgot.isPending}>
                {forgot.isPending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                Enviar enlace
              </Button>
            </form>
          )}

          <p className="mt-4 text-center text-xs text-muted-foreground">
            <Link to="/login" className="font-medium text-primary hover:underline">
              Volver a iniciar sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
